export const NAVIGATOR_BIOMETRIC_KEY = 'reso_navigator_biometrics_v1';

export interface BiometricProfile {
  age?: number | null;
  sex?: 'male' | 'female' | 'other' | null;
  height_cm?: number | null;
  weight_kg?: number | null;
  pregnant?: boolean;
  lactating?: boolean;
  allergies?: string[];
  updatedAt?: string;
}

export const emptyBiometricProfile: BiometricProfile = {
  age: null,
  sex: null,
  height_cm: null,
  weight_kg: null,
  pregnant: false,
  lactating: false,
  allergies: [],
};

export function loadBiometricProfile(): BiometricProfile | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(NAVIGATOR_BIOMETRIC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as BiometricProfile;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveBiometricProfile(profile: BiometricProfile) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(
    NAVIGATOR_BIOMETRIC_KEY,
    JSON.stringify({ ...profile, updatedAt: new Date().toISOString() }),
  );
}

export function clearBiometricProfile() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(NAVIGATOR_BIOMETRIC_KEY);
}

/** True when at least one field is usable for dose personalization. */
export function hasBiometricData(profile: BiometricProfile | null | undefined): boolean {
  if (!profile) return false;
  return Boolean(
    profile.age ||
      profile.sex ||
      profile.height_cm ||
      profile.weight_kg ||
      profile.pregnant ||
      profile.lactating ||
      (profile.allergies && profile.allergies.length > 0),
  );
}

/** BMI rounded to one decimal, or null when height/weight are incomplete. */
export function computeBmi(profile: BiometricProfile | null | undefined): number | null {
  if (!profile?.height_cm || !profile?.weight_kg) return null;
  const m = profile.height_cm / 100;
  if (m <= 0) return null;
  return Number((profile.weight_kg / (m * m)).toFixed(1));
}
